import { normalizeDataChangeDisplayVerb } from "../Chat/scoutDataChangeProposal";
import {
  buildScoutInternalEventMessage,
  parseRespondedScoutInternalEventsFromMessages,
  SCOUT_INTERNAL_EVENT_PREFIX,
  type ChatMessageLike,
  type ScoutInternalEventStatus,
} from "./scoutInternalEvents";

export type DataChangeProposalEventStatus = ScoutInternalEventStatus;

export type DataChangeProposalEventItem = {
  proposalId: string;
  /** Short human-readable label for the change (card title). */
  title: string;
  status: DataChangeProposalEventStatus;
};

function buildConfirmedDetail(title: string, proposalId: string): string {
  return (
    `User accepted ${title} (proposalId=${proposalId}). ` +
    `Call execute_graphql with approvedProposalId=${proposalId} to apply the exact staged mutation. ` +
    `Do not re-propose the same mutation. After the apply tool result succeeds, confirm what changed in prose.`
  );
}

function buildDeclinedDetail(title: string): string {
  return (
    `Rejected ${title}. ` +
    `Do not call execute_graphql with this proposalId. Adapt or ask how the user wants to proceed; ` +
    `do not re-propose the same mutation unless they ask.`
  );
}

/**
 * Build the synthetic user message for one or more data-change resolutions.
 * Multiple items become multiple `[scout-internal-event]` lines in one
 * message so Accept all / Reject all / mixed per-card decisions trigger a
 * single Scout turn while each proposalId rehydrates independently from
 * history. Accept does not apply GraphQL — the agent must execute with
 * approvedProposalId.
 */
export function buildDataChangeProposalEventMessage(input: {
  proposals: DataChangeProposalEventItem[];
}): string {
  if (input.proposals.length === 0) {
    return "";
  }

  return input.proposals
    .map((proposal) => {
      if (proposal.status === "confirmed") {
        return buildScoutInternalEventMessage({
          kind: "data-change-proposal",
          status: "confirmed",
          proposalId: proposal.proposalId,
          detail: buildConfirmedDetail(proposal.title, proposal.proposalId),
        });
      }
      return buildScoutInternalEventMessage({
        kind: "data-change-proposal",
        status: "declined",
        proposalId: proposal.proposalId,
        detail: buildDeclinedDetail(proposal.title),
      });
    })
    .join("\n");
}

/**
 * Scan persisted history for data-change-proposal events and return
 * `proposalId -> status`.
 */
export function parseRespondedDataChangeProposalsFromMessages(
  messages: ReadonlyArray<ChatMessageLike>
): Record<string, DataChangeProposalEventStatus> {
  return parseRespondedScoutInternalEventsFromMessages(messages, "data-change-proposal");
}

/**
 * Prefix-only event (not in SCOUT_INTERNAL_EVENT_KINDS). Card Accept/Reject
 * state stays on the dataChanges row; the prefix still hides this line in
 * the bubble while the visible request above it stays in chat.
 */
export const DATA_CHANGE_STALE_RETRY_KIND = "data-change-stale-retry";
export const DATA_CHANGE_STALE_RETRY_STATUS = "requested";

export const DATA_CHANGE_STALE_RETRY_USER_MESSAGE = "Revert this data change";
export const DATA_CHANGE_STALE_RETRY_USER_MESSAGE_PLURAL = "Revert these data changes";

export type DataChangeStaleRetryItem = {
  proposalId: string;
  verb?: string;
  entityLabel?: string;
  entityType?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readOptionalNonEmptyString(
  value: Record<string, unknown>,
  key: string
): string | undefined {
  const candidate = value[key];
  if (typeof candidate !== "string" || candidate.length === 0) return undefined;
  return candidate;
}

/** Pull verb/label fields off a stored data-change `diff` (unknown JSON). */
export function staleRetryItemFromDataChange(
  proposalId: string,
  diff: unknown
): DataChangeStaleRetryItem {
  if (!isRecord(diff)) return { proposalId };
  return {
    proposalId,
    verb: readOptionalNonEmptyString(diff, "verb"),
    entityLabel: readOptionalNonEmptyString(diff, "entityLabel"),
    entityType: readOptionalNonEmptyString(diff, "entityType"),
  };
}

function uniqueStaleRetryItems(items: DataChangeStaleRetryItem[]): DataChangeStaleRetryItem[] {
  const unique: DataChangeStaleRetryItem[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    if (item.proposalId.length === 0 || seen.has(item.proposalId)) continue;
    seen.add(item.proposalId);
    unique.push(item);
  }
  return unique;
}

function staleRetryLabel(item: DataChangeStaleRetryItem): string | undefined {
  if (item.entityLabel && item.entityLabel.length > 0) return item.entityLabel;
  if (item.entityType && item.entityType.length > 0) return item.entityType;
  return undefined;
}

function formatDataChangeStaleRetryPhrase(item: DataChangeStaleRetryItem): string | undefined {
  const verb = normalizeDataChangeDisplayVerb(item.verb);
  if (!verb) return undefined;
  const verbLower = verb.toLowerCase();
  const label = staleRetryLabel(item);
  if (!label) return `Revert ${verbLower}`;
  if (verb === "Update") return `Revert ${verbLower} to ${label}`;
  return `Revert ${verbLower} ${label}`;
}

function formatDataChangeStaleRetryUserLine(items: DataChangeStaleRetryItem[]): string {
  const phrases: string[] = [];
  for (const item of items) {
    const phrase = formatDataChangeStaleRetryPhrase(item);
    if (phrase) phrases.push(phrase);
  }
  if (phrases.length > 0) return phrases.join(". ");
  if (items.length === 1) return DATA_CHANGE_STALE_RETRY_USER_MESSAGE;
  return DATA_CHANGE_STALE_RETRY_USER_MESSAGE_PLURAL;
}

function buildStaleRetryDetail(proposalId: string): string {
  return (
    `Undo could not reverse this applied change (proposalId=${proposalId}) because the data ` +
    `was updated after Scout applied it (stale). Re-read the current records for this change. ` +
    `Propose a NEW data-change proposal that reverses the original intent while keeping later ` +
    `edits. Do not call any undo mutation. Do not replay the original reverse blindly.`
  );
}

/**
 * Visible user request plus one hidden event line per change id so Scout
 * knows which applied changes to compensate. Put the sentence first so the
 * bubble stays visible (`isSyntheticInternalEventMessage` only hides when
 * the text starts with the sentinel).
 */
export function buildDataChangeStaleRetryMessage(items: DataChangeStaleRetryItem[]): string {
  const unique = uniqueStaleRetryItems(items);
  if (unique.length === 0) return "";
  const events = unique.map(
    (item) =>
      `${SCOUT_INTERNAL_EVENT_PREFIX} ${DATA_CHANGE_STALE_RETRY_KIND}/` +
      `${DATA_CHANGE_STALE_RETRY_STATUS} proposalId=${item.proposalId}: ${buildStaleRetryDetail(item.proposalId)}`
  );
  return [formatDataChangeStaleRetryUserLine(unique), ...events].join("\n");
}
